import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Bar, BarChart, CartesianGrid, Legend, Line, LineChart,
    ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
    Eye, Users, ShoppingCart, Target, CurrencyDollar, MagnifyingGlass,
    Phone, Envelope, ArrowsClockwise, GlobeHemisphereWest, X,
} from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api, authHeaders } from "@/lib/api";
import PageHeader from "@/admin/components/PageHeader";
import StatCard from "@/admin/components/StatCard";

const PRESETS = [
    { id: "today", label: "Hoy", days: 1 },
    { id: "week", label: "7 días", days: 7 },
    { id: "month", label: "30 días", days: 30 },
    { id: "ytd", label: "Año", days: -1 },
];

function todayISO() {
    return new Date().toISOString().slice(0, 10);
}
function daysAgo(n) {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
}
function startOfYear() {
    return `${new Date().getFullYear()}-01-01`;
}

export default function Analytics() {
    const [from, setFrom] = useState(daysAgo(6));
    const [to, setTo] = useState(todayISO());
    const [data, setData] = useState(null);
    const [customers, setCustomers] = useState({ items: [], total: 0 });
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState("last_order_at");
    const [openCustomer, setOpenCustomer] = useState(null);
    const [vitals, setVitals] = useState(null);

    const loadAnalytics = () => {
        api.get(`/admin/analytics?date_from=${from}&date_to=${to}`, { headers: authHeaders() })
            .then((r) => setData(r.data))
            .catch(() => toast.error("Error al cargar métricas"));
    };
    const loadCustomers = () => {
        const q = search.trim();
        api.get(`/admin/customers?sort=${sort}${q ? `&q=${encodeURIComponent(q)}` : ""}`, { headers: authHeaders() })
            .then((r) => setCustomers(r.data))
            .catch(() => {});
    };
    const loadVitals = () => {
        api.get(`/admin/analytics/web-vitals?days=7`, { headers: authHeaders() })
            .then((r) => setVitals(r.data))
            .catch(() => {});
    };
    useEffect(() => { loadAnalytics(); }, [from, to]); // eslint-disable-line
    useEffect(() => { loadCustomers(); }, [sort, search]); // eslint-disable-line
    useEffect(() => { loadVitals(); }, []);

    const setPreset = (p) => {
        if (p.id === "today") { setFrom(todayISO()); setTo(todayISO()); }
        else if (p.id === "ytd") { setFrom(startOfYear()); setTo(todayISO()); }
        else { setFrom(daysAgo(p.days - 1)); setTo(todayISO()); }
    };

    const t = data?.totals || {};

    return (
        <div data-testid="admin-analytics">
            <PageHeader
                kicker="Tráfico y clientes"
                title="Analítica"
                action={
                    <button
                        data-testid="refresh-analytics"
                        onClick={() => { loadAnalytics(); loadCustomers(); }}
                        className="inline-flex items-center gap-2 rounded-full bg-foreground text-background px-4 py-2 text-xs font-bold uppercase tracking-widest hover:opacity-90"
                    >
                        <ArrowsClockwise size={12} weight="bold" /> Actualizar
                    </button>
                }
            />

            {/* Date range */}
            <Card className="p-4 mb-6">
                <div className="flex flex-wrap items-end gap-3" data-testid="an-date-range">
                    <div className="flex gap-2 flex-wrap">
                        {PRESETS.map((p) => (
                            <button
                                key={p.id}
                                data-testid={`an-preset-${p.id}`}
                                onClick={() => setPreset(p)}
                                className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest border-2 border-border bg-white hover:border-foreground"
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-end gap-2">
                        <RangeInput label="Desde" value={from} onChange={setFrom} testId="an-from" />
                        <RangeInput label="Hasta" value={to} onChange={setTo} testId="an-to" />
                    </div>
                </div>
            </Card>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="an-kpis">
                <StatCard icon={Eye} kicker="Visitas" value={t.visits || 0} />
                <StatCard icon={Users} kicker="Visitantes únicos" value={t.unique_visitors || 0} />
                <StatCard icon={ShoppingCart} kicker="Órdenes web" value={t.web_orders || 0} />
                <StatCard icon={Target} kicker="Conversión" value={`${(t.conversion_rate || 0).toFixed(1)}%`} />
                <StatCard icon={CurrencyDollar} kicker="Ingreso web" value={`$${(t.revenue_web || 0).toFixed(2)}`} />
                <StatCard icon={CurrencyDollar} kicker="Ingreso total" value={`$${(t.revenue_total || 0).toFixed(2)}`} />
            </div>

            {/* Charts */}
            <div className="grid lg:grid-cols-2 gap-4 mb-6">
                {/* Visits vs orders */}
                <Card className="p-5">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-3">
                        Visitas vs Órdenes
                    </p>
                    <div className="h-64" data-testid="chart-visits">
                        {data?.by_day?.length ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={data.by_day}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                    <YAxis tick={{ fontSize: 10 }} />
                                    <Tooltip />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                    <Line type="monotone" dataKey="visits" name="Visitas" stroke="#3b82f6" strokeWidth={2} dot={false} />
                                    <Line type="monotone" dataKey="unique" name="Únicos" stroke="#10b981" strokeWidth={2} dot={false} />
                                    <Line type="monotone" dataKey="orders" name="Órdenes" stroke="#f97316" strokeWidth={2.5} dot={{ r: 3 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        ) : <Empty />}
                    </div>
                </Card>

                {/* Daily revenue */}
                <Card className="p-5">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-3">
                        Ingreso web por día
                    </p>
                    <div className="h-64" data-testid="chart-revenue">
                        {data?.by_day?.length ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={data.by_day}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                    <YAxis tickFormatter={(v) => `$${v}`} tick={{ fontSize: 10 }} />
                                    <Tooltip formatter={(v) => `$${Number(v).toFixed(2)}`} />
                                    <Bar dataKey="revenue" name="Ingreso" fill="#E87811" radius={[6, 6, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : <Empty />}
                    </div>
                </Card>
            </div>

            {/* Sources & pages */}
            <div className="grid lg:grid-cols-3 gap-4 mb-6">
                <Card className="p-5">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-3">
                        Top páginas
                    </p>
                    <List items={data?.top_pages} kField="page" vField="views" />
                </Card>
                <Card className="p-5">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-3">
                        Fuentes de tráfico
                    </p>
                    <List items={data?.top_referrers} kField="source" vField="visits" />
                </Card>
                <Card className="p-5">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-3">
                        Idioma del visitante
                    </p>
                    <div className="space-y-2 text-sm">
                        {Object.entries(data?.languages || {}).length === 0 ? (
                            <p className="text-muted-foreground">Sin datos.</p>
                        ) : (
                            Object.entries(data?.languages || {})
                                .sort((a, b) => b[1] - a[1])
                                .map(([lng, n]) => (
                                    <div key={lng} className="flex justify-between items-center">
                                        <span className="font-bold uppercase">
                                            <GlobeHemisphereWest size={14} weight="bold" className="inline mr-1.5 text-blue-600" />
                                            {lng || "?"}
                                        </span>
                                        <span className="font-display font-black">{n}</span>
                                    </div>
                                ))
                        )}
                    </div>
                </Card>
            </div>

            {/* Performance / Core Web Vitals */}
            <WebVitalsCard vitals={vitals} onReload={loadVitals} />

            {/* Customer database */}
            <Card className="p-5">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <div>
                        <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
                            Clientes capturados
                        </p>
                        <p className="font-display font-black text-2xl">{customers.total}</p>
                    </div>
                    <div className="flex gap-2 flex-wrap items-center">
                        <div className="relative">
                            <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                data-testid="customer-search"
                                placeholder="Buscar nombre, email o teléfono…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-9 h-9 w-72"
                            />
                        </div>
                        <select
                            data-testid="customer-sort"
                            value={sort}
                            onChange={(e) => setSort(e.target.value)}
                            className="h-9 px-3 rounded-md border border-input bg-background text-xs font-bold"
                        >
                            <option value="last_order_at">Más reciente</option>
                            <option value="total_spent">Mayor gasto</option>
                            <option value="total_orders">Más órdenes</option>
                            <option value="first_order_at">Más antiguo</option>
                        </select>
                    </div>
                </div>

                {customers.items.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-6 text-center">
                        Aún no hay clientes capturados. Cuando alguien compre por la web o el POS aparecerá aquí.
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="customer-table">
                            <thead className="bg-muted/40">
                                <tr className="text-left text-[11px] uppercase tracking-widest text-muted-foreground">
                                    <th className="px-3 py-2">Cliente</th>
                                    <th className="px-3 py-2">Contacto</th>
                                    <th className="px-3 py-2 text-right">Órdenes</th>
                                    <th className="px-3 py-2 text-right">Gasto total</th>
                                    <th className="px-3 py-2 text-right">Ticket</th>
                                    <th className="px-3 py-2">Última</th>
                                </tr>
                            </thead>
                            <tbody>
                                {customers.items.map((c) => (
                                    <tr
                                        key={c.key}
                                        data-testid={`customer-row-${c.id}`}
                                        onClick={() => setOpenCustomer(c)}
                                        className="border-t border-border hover:bg-muted/40 cursor-pointer"
                                    >
                                        <td className="px-3 py-2 font-bold">{c.name || "Walk-in"}</td>
                                        <td className="px-3 py-2 text-muted-foreground">
                                            {c.email && (
                                                <span className="inline-flex items-center gap-1 mr-3">
                                                    <Envelope size={12} weight="bold" /> {c.email}
                                                </span>
                                            )}
                                            {c.phone && (
                                                <span className="inline-flex items-center gap-1">
                                                    <Phone size={12} weight="bold" /> {c.phone}
                                                </span>
                                            )}
                                            {!c.email && !c.phone && (
                                                <span className="text-xs">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-right font-bold">{c.total_orders}</td>
                                        <td className="px-3 py-2 text-right font-display font-black">
                                            ${(c.total_spent || 0).toFixed(2)}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            ${(c.total_orders ? c.total_spent / c.total_orders : 0).toFixed(2)}
                                        </td>
                                        <td className="px-3 py-2 text-xs text-muted-foreground">
                                            {(c.last_order_at || "").replace("T", " ").slice(0, 16)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {/* Customer detail */}
            <CustomerDetail
                customer={openCustomer}
                onClose={() => setOpenCustomer(null)}
            />
        </div>
    );
}

function RangeInput({ label, value, onChange, testId }) {
    return (
        <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold block mb-1">
                {label}
            </label>
            <Input
                type="date"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
                className="h-9 w-40"
            />
        </div>
    );
}

function Empty() {
    return (
        <div className="h-full grid place-items-center">
            <p className="text-sm text-muted-foreground">Sin datos en este rango.</p>
        </div>
    );
}

function List({ items, kField, vField }) {
    if (!items?.length) return <p className="text-sm text-muted-foreground">Sin datos.</p>;
    const max = Math.max(...items.map((i) => i[vField] || 0));
    return (
        <div className="space-y-2 text-sm">
            {items.slice(0, 8).map((it, i) => (
                <div key={i}>
                    <div className="flex justify-between mb-0.5">
                        <span className="font-bold truncate max-w-[70%]">{it[kField] || "—"}</span>
                        <span className="font-display font-black">{it[vField]}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                            className="h-full bg-primary"
                            style={{ width: `${(it[vField] / max) * 100}%` }}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
}

function CustomerDetail({ customer, onClose }) {
    if (!customer) return null;
    return <CustomerDetailInner customer={customer} onClose={onClose} key={customer.key} />;
}

function CustomerDetailInner({ customer, onClose }) {
    const [detail, setDetail] = useState(null);
    useEffect(() => {
        api.get(`/admin/customers/${encodeURIComponent(customer.key)}`, { headers: authHeaders() })
            .then((r) => setDetail(r.data))
            .catch(() => toast.error("Error al cargar"));
    }, [customer.key]);
    return (
        <Dialog open={!!customer} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-2xl" data-testid="customer-detail">
                <DialogHeader>
                    <DialogTitle className="font-display font-black text-2xl">
                        {customer.name || "Walk-in"}
                    </DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-3 gap-3 mb-4">
                    <Mini k="Órdenes" v={customer.total_orders} />
                    <Mini k="Gasto total" v={`$${(customer.total_spent || 0).toFixed(2)}`} />
                    <Mini
                        k="Ticket prom."
                        v={`$${(customer.total_orders ? customer.total_spent / customer.total_orders : 0).toFixed(2)}`}
                    />
                </div>
                <div className="text-sm space-y-1 mb-4">
                    {customer.email && <p><b>Email:</b> {customer.email}</p>}
                    {customer.phone && <p><b>Teléfono:</b> {customer.phone}</p>}
                    <p className="text-muted-foreground text-xs">
                        Primera compra: {(customer.first_order_at || "").slice(0, 10)} · Última: {(customer.last_order_at || "").slice(0, 10)}
                    </p>
                </div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">
                    Historial de órdenes
                </p>
                <div className="max-h-80 overflow-y-auto border border-border rounded-xl">
                    {(detail?.orders || []).length === 0 ? (
                        <p className="text-sm text-muted-foreground p-4">Cargando…</p>
                    ) : (
                        <table className="w-full text-sm">
                            <tbody>
                                {detail.orders.map((o) => (
                                    <tr key={o.id} className="border-t border-border first:border-t-0">
                                        <td className="px-3 py-2 font-bold">#{o.id.slice(-6).toUpperCase()}</td>
                                        <td className="px-3 py-2 text-xs text-muted-foreground">
                                            {(o.created_at || "").replace("T", " ").slice(0, 16)}
                                        </td>
                                        <td className="px-3 py-2 text-xs uppercase">{o.channel || "web"}</td>
                                        <td className="px-3 py-2 text-right font-display font-black">
                                            ${(o.total || 0).toFixed(2)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function Mini({ k, v }) {
    return (
        <div className="bg-muted rounded-xl p-3">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{k}</p>
            <p className="font-display font-black text-lg">{v}</p>
        </div>
    );
}

// Formats a Web Vitals metric — LCP/FCP/TTFB in ms, CLS as fraction, INP in ms.
function fmtVital(name, value) {
    if (name === "CLS") return value.toFixed(3);
    if (value < 1000) return `${Math.round(value)} ms`;
    return `${(value / 1000).toFixed(2)} s`;
}

const VITAL_META = {
    LCP:  { label: "Largest Contentful Paint", hint: "< 2.5 s ideal — cuán rápido carga la imagen principal." },
    INP:  { label: "Interaction to Next Paint", hint: "< 200 ms ideal — qué tan rápido responde al hacer click." },
    CLS:  { label: "Cumulative Layout Shift",   hint: "< 0.1 ideal — el contenido no debe saltar mientras carga." },
    FCP:  { label: "First Contentful Paint",    hint: "< 1.8 s ideal — cuándo aparece el primer pixel." },
    TTFB: { label: "Time to First Byte",         hint: "< 800 ms ideal — velocidad del servidor." },
};

function WebVitalsCard({ vitals, onReload }) {
    return (
        <Card className="p-5 mb-6">
            <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
                <div>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
                        Rendimiento — Core Web Vitals (últimos 7 días)
                    </p>
                    <p className="text-[11px] text-muted-foreground italic">
                        Datos de visitantes reales. Google usa estas métricas para el ranking en búsquedas.
                    </p>
                </div>
                <button
                    data-testid="vitals-reload"
                    onClick={onReload}
                    className="text-[10px] font-bold uppercase tracking-widest text-primary hover:underline"
                >
                    Refrescar
                </button>
            </div>
            {!vitals || (vitals.metrics || []).length === 0 ? (
                <div data-testid="vitals-empty" className="text-center py-8 text-muted-foreground text-sm">
                    <p>Aún sin muestras. Los datos aparecen automáticamente cuando visitantes reales navegan el sitio en producción.</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {vitals.metrics.map((m) => {
                        const meta = VITAL_META[m.name] || { label: m.name, hint: "" };
                        const total = Math.max(1, m.good + m.needs + m.poor);
                        const goodPct = Math.round((m.good / total) * 100);
                        const status =
                            goodPct >= 75 ? { color: "text-emerald-700", bg: "bg-emerald-100", label: "Bueno" } :
                            goodPct >= 50 ? { color: "text-amber-700", bg: "bg-amber-100", label: "A mejorar" } :
                            { color: "text-red-700", bg: "bg-red-100", label: "Malo" };
                        return (
                            <div
                                key={m.name}
                                data-testid={`vital-${m.name}`}
                                className="rounded-2xl border border-border p-3"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="font-display font-black text-lg">{m.name}</span>
                                    <span className={`text-[10px] font-bold uppercase tracking-widest rounded-full px-2 py-0.5 ${status.bg} ${status.color}`}>
                                        {status.label}
                                    </span>
                                </div>
                                <p className={`font-display font-black text-2xl mt-1 ${status.color}`}>
                                    {fmtVital(m.name, m.p75)}
                                </p>
                                <p className="text-[10px] text-muted-foreground mt-0.5">
                                    p75 · {m.count} muestras
                                </p>
                                {/* Rating bar */}
                                <div className="flex h-1.5 rounded-full overflow-hidden mt-2 bg-muted">
                                    <span className="bg-emerald-500" style={{ width: `${(m.good / total) * 100}%` }} />
                                    <span className="bg-amber-500" style={{ width: `${(m.needs / total) * 100}%` }} />
                                    <span className="bg-red-500" style={{ width: `${(m.poor / total) * 100}%` }} />
                                </div>
                                <p className="text-[10px] text-muted-foreground mt-1.5 leading-tight">
                                    {meta.hint}
                                </p>
                            </div>
                        );
                    })}
                </div>
            )}
        </Card>
    );
}
